import type { RowSelectionConfig } from './types';

/** Ids of the rows in the current page that are eligible for selection. */
export function selectableIds<T>(
  data: T[],
  getRowId: (row: T) => string,
  isRowSelectable?: RowSelectionConfig<T>['isRowSelectable'],
): string[] {
  return data.filter((row) => isRowSelectable?.(row) ?? true).map(getRowId);
}

/** Next Set after toggling one row's checkbox — pure, ready to hand to onSelectionChange. */
export function toggleRowSelection(selectedIds: ReadonlySet<string>, rowId: string): Set<string> {
  const next = new Set(selectedIds);
  if (next.has(rowId)) next.delete(rowId);
  else next.add(rowId);
  return next;
}

/**
 * Next Set after toggling the header select-all checkbox. Operates only on
 * the current page's selectable ids — ids belonging to other pages (already
 * in selectedIds) are left untouched either way, so cross-page selections
 * survive pagination.
 */
export function toggleSelectAll(selectedIds: ReadonlySet<string>, pageIds: string[]): Set<string> {
  const next = new Set(selectedIds);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => next.has(id));
  if (allSelected) pageIds.forEach((id) => next.delete(id));
  else pageIds.forEach((id) => next.add(id));
  return next;
}

/** Checkbox tri-state for the header select-all control. */
export function selectAllState(selectedIds: ReadonlySet<string>, pageIds: string[]): boolean | 'indeterminate' {
  if (pageIds.length === 0) return false;
  const selectedCount = pageIds.filter((id) => selectedIds.has(id)).length;
  if (selectedCount === 0) return false;
  if (selectedCount === pageIds.length) return true;
  return 'indeterminate';
}
